import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CmsService, I18nModule, CmsProductCarouselComponent as model, PageContext, PageType, Product, ProductScope, ProductService, UrlModule } from '@spartacus/core';
import { Observable, Subject, forkJoin, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CarouselModule, CmsComponentData, MediaModule, PageComponentModule } from '@spartacus/storefront';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { GenProductStateService } from 'src/app/generac/services/product-state.service';
import { GenCustomerService } from 'src/app/generac/services/customer.service';
import { GaItem, GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { CommonModule } from '@angular/common';
import { GenProductCarouselItemComponent } from './product-carousel-item/product-carousel-item.component';
import { isEqual } from 'src/app/generac/shared/helpers/is-equal';

@Component({
  selector: 'gen-product-carousel',
  templateUrl: './product-carousel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CarouselModule,
    MediaModule,
    RouterModule,
    UrlModule,
    I18nModule,
    PageComponentModule,
    GenProductCarouselItemComponent,
  ],
})
export class GenProductCarouselComponent implements OnInit, OnDestroy {
  products: Product[] = [];
  gaListName: string;

  protected readonly PRODUCT_SCOPE = [
    ProductScope.LIST,
    ProductScope.DETAILS,
  ];

  private componentData$: Observable<model> = this.componentData.data$.pipe(
    filter((data) => Boolean(data))
  );

  /**
   * returns an Observable string for the title.
   */
  title$: Observable<string | undefined> = this.componentData$.pipe(
    map((data) => data.title)
  );

  /**
   * Observable that holds an Array of Observables. This is done, so that
   * the component UI could consider to lazy load the UI components when they're
   * in the viewpoint.
   */
  items$: Observable<Observable<Product | undefined>[]> =
    this.componentData$.pipe(
      distinctUntilChanged(isEqual),
      map((data) => {
        this.gaListName = data.title;
        return data.productCodes?.trim().split(' ') ?? []
      }),
      map((codes) =>
        codes.map((code: string) =>
          this.productService.get(code, [...this.PRODUCT_SCOPE])
            .pipe(
              distinctUntilChanged((prev,next) => prev && prev?.code == next?.code),
              tap((product) => {
                if (product) {
                  this.products.push(product);
                  if (this.products.length == codes.length) {
                    this.googleAnalyticsService.sendViewItemListEvent(this.products, this.gaListName);
                    this.products = [];
                  }
                }
              })
            )
        )
      )
    );

  private unsubscribe$ = new Subject();

  constructor(
    protected componentData: CmsComponentData<model>,
    protected productService: ProductService,
    protected cmsService: CmsService,
    protected customerService: GenCustomerService,
    protected genProductStateService: GenProductStateService,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    protected activatedRoute: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    merge(
      this.componentData.data$,
      this.customerService.getB2bUnitChanged().pipe(
        map(() => {
          let pageContext: PageContext;
          const snapshotUrl = this.activatedRoute.snapshot;
          if (snapshotUrl?.data) {
            pageContext = {
              id: snapshotUrl?.params?.productCode ? snapshotUrl?.params?.productCode : snapshotUrl?.params?.categoryCode,
              type: snapshotUrl.data?.cxRoute === 'category' ? PageType.CATEGORY_PAGE : PageType.PRODUCT_PAGE,
            }
          } else {
            pageContext = {
              id: 'current',
            }
          }

          return this.cmsService.refreshComponent(this.componentData.uid, pageContext);
        }),
        tap(() => this.genProductStateService.clearProductScope())
      )
    )
      .pipe(filter(Boolean), takeUntil(this.unsubscribe$))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }
}
